'use client';

import {
  type ClipboardEvent,
  type ComponentProps,
  type KeyboardEvent,
  useRef,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { Input } from '../ui/input';
import { useFieldControl } from './field';
import { Tag } from './tag';

export type TagRejectReason = 'invalid' | 'duplicate' | 'max';

export type TagInputProps = Omit<
  ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange' | 'size'
> & {
  /** The tags (controlled). */
  value?: string[];
  /** Initial tags when uncontrolled. */
  defaultValue?: string[];
  /** Called with the new tags after one is added or removed. */
  onValueChange?: (value: string[]) => void;
  /** The text being typed (controlled), e.g. to drive a suggestion list. */
  inputValue?: string;
  /** Initial text when uncontrolled. */
  defaultInputValue?: string;
  onInputValueChange?: (text: string) => void;
  /** Most tags allowed; further entries are rejected with reason "max". */
  max?: number;
  /** Return false to reject a tag (reason "invalid"); the text stays in the input. */
  validate?: (tag: string) => boolean;
  /** A tag was rejected - show why in the Field `error`. */
  onReject?: (tag: string, reason: TagRejectReason) => void;
  /** Class for the outer box (width, margins). */
  className?: string;
};

/**
 * Figma Tag Input (92:208): the Text Input box (border/strong, radius/md,
 * bg/surface; Focus border/focus 1.5 + Focus/Input; Error status/danger/fg
 * 1.5; Disabled bg/disabled + border/default) wrapping Small Tags. 8px sides,
 * 7px top/bottom + the 1px border = 40 tall on one line, gap 6 both ways.
 * The text entry is 80 wide (grows to fill the line), 4px left padding,
 * Body/Medium with a border/focus caret.
 */
const tagInput = tv({
  slots: {
    root: [
      'group/tag-input flex min-h-10 w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border px-2 py-1.75',
      'border-border-strong bg-bg-surface text-text-primary',
      'transition-[border-color,box-shadow] duration-[120ms] ease-out',
      'hover:border-border-strong-hover',
      'has-[input:focus-visible]:border-border-focus has-[input:focus-visible]:shadow-focus-input',
      'has-[input:focus-visible]:inset-ring-[0.5px] has-[input:focus-visible]:inset-ring-border-focus',
      'has-[input[aria-invalid=true]]:border-status-danger-fg',
      'has-[input[aria-invalid=true]]:inset-ring-[0.5px] has-[input[aria-invalid=true]]:inset-ring-status-danger-fg',
      'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:border-border-default has-[input:disabled]:bg-bg-disabled',
    ],
    input: 'text-body-md h-6 w-20 grow pl-1 caret-border-focus',
  },
});

const split = (text: string) =>
  text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

/**
 * Multi-value text entry (Figma: Components ↳ Tag ↳ Tag Input). Enter or
 * comma turns the typed text into a Tag; Backspace in the empty input removes
 * the last one; pasted comma-separated text splits into tags. Inside a Field
 * it picks up id, description, invalid and disabled. Additions and removals
 * are announced politely.
 */
export function TagInput({
  value: valueProp,
  defaultValue = [],
  onValueChange,
  inputValue: inputProp,
  defaultInputValue = '',
  onInputValueChange,
  max,
  validate,
  onReject,
  className,
  onKeyDown,
  onPaste,
  ...props
}: TagInputProps) {
  const [valueState, setValueState] = useState(defaultValue);
  const value = valueProp ?? valueState;
  const [textState, setTextState] = useState(defaultInputValue);
  const text = inputProp ?? textState;
  const [announcement, setAnnouncement] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const control = useFieldControl(props);
  const disabled = Boolean(control.disabled);
  const styles = tagInput();

  const setText = (next: string) => {
    setTextState(next);
    onInputValueChange?.(next);
  };
  const setValue = (next: string[]) => {
    setValueState(next);
    onValueChange?.(next);
  };

  /** Adds what it can; returns the entries that were rejected. */
  const add = (entries: string[]) => {
    const next = [...value];
    const rejected: string[] = [];
    for (const tag of entries) {
      const reason: TagRejectReason | null = next.includes(tag)
        ? 'duplicate'
        : max !== undefined && next.length >= max
          ? 'max'
          : validate && !validate(tag)
            ? 'invalid'
            : null;
      if (reason) {
        rejected.push(tag);
        onReject?.(tag, reason);
      } else next.push(tag);
    }
    if (next.length !== value.length) {
      setValue(next);
      setAnnouncement(`Added ${next.slice(value.length).join(', ')}`);
    }
    return rejected;
  };

  const remove = (index: number) => {
    const tag = value[index];
    setValue(value.filter((_, i) => i !== index));
    setAnnouncement(`Removed ${tag}`);
    inputRef.current?.focus();
  };

  const commit = (raw: string) => {
    const rejected = add(split(raw));
    setText(rejected.join(', '));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented || e.nativeEvent.isComposing) return;
    if ((e.key === 'Enter' || e.key === ',') && text.trim()) {
      e.preventDefault();
      commit(text);
    } else if (e.key === ',') {
      e.preventDefault();
    } else if (e.key === 'Backspace' && text === '' && value.length) {
      e.preventDefault();
      remove(value.length - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    onPaste?.(e);
    const pasted = e.clipboardData.getData('text');
    if (e.defaultPrevented || !pasted.includes(',')) return;
    e.preventDefault();
    commit(text + pasted);
  };

  return (
    <div
      data-slot="tag-input"
      className={styles.root({ className })}
      onClick={(e) => {
        if (e.target === e.currentTarget) inputRef.current?.focus();
      }}
    >
      {value.map((tag, i) => (
        <Tag key={tag} disabled={disabled} onRemove={() => remove(i)}>
          {tag}
        </Tag>
      ))}
      <Input
        ref={inputRef}
        {...control}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className={styles.input()}
      />
      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
